import moment, { Moment } from 'moment';
import { Country } from 'react-phone-number-input';
import {
  CategoryType,
  DELIVERY_ZONE_GEOMETRY_TYPES,
  DeliveryMode,
  DeliveryProvider,
  DeliveryStatus,
  DropoffAction,
  EntryType,
  GOOGLE_REVIEW_STATUS_TYPES,
  ModifierGroupType,
  ORDER_FULFILLMENT_STATUSES,
  ORDER_METHODS,
  ORDER_PAYMENT_STATUSES,
  OTP_DELIVERY_CHANNELS,
  PaymentMethod,
  PaymentMethodBrand,
  RoomServiceLocationType,
  SmartPricingRuleModifierType,
  TaxBehaviour,
} from './Constants';
import {
  COUPON_TYPES,
  EventHelperTypes,
  ORDERS_PAUSED_REASON,
  PROMO_CODE_DISCOUNT_TYPES,
  PROMO_CODE_TYPES,
} from './Constants';
import { FeedbackLabelTypes } from './Types';
import { ReactElement } from 'react';
import { BasketLineItem, BasketLineItemModifier } from '../../store/types';
import { Collection } from 'collect.js';

export type IMap<V, K extends string | number = string | number> = {
  [index in K]: V;
};

export interface ConfigInterface {
  primary_color: string | null;
  logo_url: string | null;
}

export interface UserInterface {
  id: number;
  first_name: string;
  last_name: string;
  created_at: string | null;
}

export interface Franchise {
  id: number;
  name: string;
  slug: string;
  logo_url: string;
  logos: LogoInterface[];
  primary_color: string;
  country?: string;
  head_scripts: string | null;
  body_noscripts: string | null;
  facebook_pixel_id: string | null;
  google_analytics_id: string | null;
  has_multiple_companies: boolean;
  companies: CompanyDetailsInterface[];
}

export interface CompanyLocation {
  name?: null | string;
  day?: null | number;
  hour?: null | number;
  minute?: null | number;
  address: string;
  city: string;
  zip_code: null | string;
  country: Country;
  lat: number;
  lon: number;
}

export interface CompanyDetailsInterface {
  // General & profile fields
  id: number;
  currency: string;
  stripe_id: string;
  slug: string;
  name: string;
  email: null | string;
  phone_number: string;
  logo_url: null | string;
  pictures: string[];
  primary_color: null | string;
  instagram_link: string | null;
  website_link: string | null;
  impressum_link: string | null;
  legal_address: string | null;
  has_savings_banner: boolean;

  // Tax fields
  tax_behaviour: TaxBehaviour;
  tax_percentage: number;

  // Payment method & checkout fields
  supported_payment_methods: PaymentMethod[];
  hidden_payment_methods: IMap<ORDER_METHODS[] | string[], PaymentMethod>;
  has_in_store_card_payments: boolean;
  overwrite_cash_payments_to_invoice: boolean;
  has_paypal_upcharge: boolean;
  default_tip_percentage: number | null;

  // Location fields
  has_multiple_locations: boolean;
  locations: CompanyLocation[];
  location_name: null | string;
  address: string;
  city: string;
  state: string;
  country: Country;
  zip_code: null | string;
  lat: number;
  lon: number;

  // Google place fields
  google_rating: string;
  google_rates_count: number;
  google_places_id: string;

  // Custom messages fields
  pickup_customer_message_en: null | string;
  pickup_customer_message_de: null | string;
  rewards_info_header_en: string | null;
  rewards_info_description_en: string | null;
  rewards_info_header_de: string | null;
  rewards_info_description_de: string | null;
  scheduled_order_option_title: string | null;

  // General order setting fields
  has_live_orders: boolean;
  hidden_order_methods: null | ORDER_METHODS[];
  has_asap_orders: boolean;
  has_scheduled_orders: boolean;
  has_pre_order: boolean;
  scheduled_orders_time_slot_interval: number;
  has_order_instructions: boolean;
  order_instructions_title: string | null;

  // Pickup fields
  has_pickup: boolean;
  average_order_preparation_time?: AverageOrderPreparationTimeInterface | null;
  last_order_gap_pickup: number;
  min_schedule_ahead_time_pickup: number;

  // Delivery fields
  has_delivery: boolean;
  delivery_enabled: boolean;
  delivery_mode: DeliveryMode;
  average_order_delivery_time?: AverageOrderPreparationTimeInterface | null;
  last_order_gap_delivery: number;
  min_schedule_ahead_time_delivery: number;

  // Room service fields
  has_room_service: boolean;

  //Schedule fields
  open_now: boolean;

  // Pickup schedule fields
  has_work_schedule: boolean;
  work_schedule: OpeningHoursInterface[];
  current_work_schedule?: OpeningHoursInterface | null;
  next_work_schedule?: OpeningHoursInterface | null;

  // Delivery schedule fields
  has_delivery_schedule: boolean;
  open_now_for_delivery: boolean;
  delivery_schedule: OpeningHoursInterface[];
  current_delivery_schedule?: OpeningHoursInterface | null;
  next_delivery_schedule?: OpeningHoursInterface | null;

  // Paused orders fields
  orders_paused_until?: string | null;
  orders_pause_reason?: ORDERS_PAUSED_REASON | null;

  // Schedule exceptions
  closed_from: string | null;
  closed_until: string | null;

  // Related resources
  service_provider: ServiceProvider;
  cashback_program?: null | CashbackProgram;
  loyalty_program?: null | LoyaltyProgram;
  google_review_program?: null | LoyaltyProgram;
  smart_pricing_deals: SmartPricingRule[];
  smart_pricing_rules: SmartPricingRule[];
  delivery_zones: DeliveryZone[];
  room_service_locations: RoomServiceLocation[];
  table_number_hidden: boolean;

  // Menu
  menus: MenuInterface[];
  categories: IMap<CategoryInterface>;
  bogo_products: Collection<ProductInterface>;
  products: IMap<ProductInterface>;
  modifier_groups: IMap<ModifierGroupInterface>;
  modifiers: IMap<Modifier>;

  // Promocodes
  show_master_promo_code: boolean;
  promo_codes: CouponDetailsInterface[];
}

export interface SmartPricingRule {
  id: number;
  name: string;
  order_methods: ORDER_METHODS[];
  modifier_type: SmartPricingRuleModifierType;
  modifier_value: number;
  is_visible: boolean;
  is_global: boolean;
}

export interface DeliveryZone {
  id: number;
  name: string;
  color: string;
  is_hidden: boolean;
  min_threshold: number;
  max_threshold: null | number;
  fee: number;
  geometry_type: DELIVERY_ZONE_GEOMETRY_TYPES;
  geometry_data: google.maps.CircleOptions | google.maps.PolygonOptions | string[];
}

export interface DeliveryQuote {
  id: number;
  provider: string;
  carrier: string;
  price: number;
  customer_price: number;
  pickup_estimate: string;
  dropoff_estimate: string;
  expires_at: string;
}

export interface RoomServiceLocation {
  id: number;
  type: RoomServiceLocationType;
  name: string;
  street_name: string;
  street_number: string;
  city: string;
  zip_code: string;
  table_number_hidden: boolean;
}

export interface LogoInterface {
  height: number;
  width: number;
  url: string;
}

export interface ServiceProvider {
  name: string;
  country: string;
  website: string;
  logo_url: string;
  sender_email: string;
  support_email: string;
  stripe_key: string;
  google_analytics_id: string;
  amplitude_api_key: null | string;
}

export interface Promotion {
  id: number;
  reward: number;
  end_date: string;
}

export interface CashbackProgram extends Promotion {
  percentage: number;
  point_lifetime: number;
  welcome_bonus: null | number;
  min_spending_limit: null | number;
  max_spending_limit: null | number;
}

export interface LoyaltyProgram extends Promotion {
  reward_threshold: number;
  is_per_item: boolean;

  rewards: RewardInterface[];
}

export interface OpeningHoursInterface {
  open?: OpeningHoursScheduleInterface;
  close?: OpeningHoursScheduleInterface;
}

export interface OpeningHoursScheduleInterface {
  day: number;
  hour: number;
  minute: number;
}

export interface AverageOrderPreparationTimeInterface {
  min?: number | null;
  max?: number | null;
}

export interface MenuInterface {
  id: number;
  name: string;
  is_active: boolean;
  schedule: OpeningHoursInterface[];
  sorting_order: null | number;

  category_ids: number[];
  smart_pricing_rules?: SmartPricingRule[];
}

export interface CategoryInterface {
  id: number;
  menu_id: number;
  type: CategoryType;
  name: string;
  description?: string;
  image_url: null | string;
  product_ids: number[];
  menu_ids: number[];
  products?: ProductInterface[];
  smart_pricing_rules?: SmartPricingRule[];
}

export type OrderMethodPrices = {
  [method in ORDER_METHODS]: number;
};

export interface ProductInterface {
  id: number;
  type: EntryType.Product;
  name: string;
  base_price: number;
  original_price: number;
  actual_price: number;
  original_order_method_prices: null | OrderMethodPrices;
  actual_order_method_prices: null | OrderMethodPrices;
  discount: number;
  description: string;
  image_url: string;
  thumbnail_url: string;
  free_eligible: boolean;
  is_discounted: boolean;
  is_promotable: boolean;
  is_available: boolean;
  identifiers: string[];
  popularity_rank?: number;
  likes_count: number;
  allergens: string[];

  category_ids: number[];
  modifier_group_ids: number[];
  modifier_groups?: ModifierGroupInterface[];
}

export type OrderLineItem = BasketLineItem & {
  id: number;
  type: EntryType.Product;
  name: string;
  original_price: number;
  actual_price: number;
  product_original_price: number;
  product_actual_price: number;
  price: number;
  discount: number;
  description: string;
  image_url: string;
  thumbnail_url: string;
  is_available: boolean;

  children?: OrderLineItem[];
  modifiers: Modifier[];
  category: LineItemCategoryInterface;
};

export type Modifier = BasketLineItemModifier & {
  id: number;
  type: EntryType;
  name: string;
  original_price: number;
  actual_price: number;
  price: number;
  original_order_method_prices: null | OrderMethodPrices;
  actual_order_method_prices: null | OrderMethodPrices;
  is_available: boolean;
  is_visible: boolean;
  free_eligible: boolean;

  modifier_group_ids?: number[];
  modifier_groups?: ModifierGroupInterface[];
};

export interface LineItemCategoryInterface {
  id: number;
  name: string;
  sorting_order: number;
}

export interface ModifierId {
  id: number;
  type: EntryType;
}

export interface ModifierGroupInterface {
  id: number;
  is_required: boolean;
  is_variant: boolean;
  is_visible: boolean;
  max_count: number;
  multiply: boolean;
  name: string;
  type: ModifierGroupType;
  modifier_ids: ModifierId[];
  modifiers?: (Modifier | ProductInterface)[];
}

export interface RewardInterface {
  id: number;
  cost: number;
  discount: number;
  is_available: boolean;
}

export interface PaymentOptionPropertiesInterface {
  id: number;
  payment_method_id: PaymentMethod;
  external_payment_method_id: null | string;
  payment_method_brand: null | PaymentMethodBrand;
  payment_method_details: null | string;
  name: string;
  display_name: string;
  is_default: boolean;
  is_saved: boolean;
}

export interface EventHelperActionInterface {
  type: EventHelperTypes;
  payload?: unknown;
}

export interface RewardInterface {
  id: number;
  cost: number;
  discount: number;
  is_available: boolean;
}

export interface Delivery {
  id: number;
  status: DeliveryStatus;
  carrier: string;
  runner_name: null | string;
  runner_phone_number: null | string;
  tracking_url: null | string;
  dropoff_estimate: null | string;
}

export type OrderFulfillmentEvent = {
  id: number;
  status: ORDER_FULFILLMENT_STATUSES;
  created_at: string;
};

export type OrderFulfillment = {
  id: number;
  number: number;
  status: ORDER_FULFILLMENT_STATUSES;
  estimated_preparation_time: number;
  initial_confirmation_time: string;
  estimated_pickup_time: string;
  created_at: string;
  complete_at: string;

  events: OrderFulfillmentEvent[];
};

export type Order = InvoiceData & {
  id: number;
  company_id: number;
  uuid: string;
  payment_status: ORDER_PAYMENT_STATUSES;
  delivery_provider: DeliveryProvider;
  coupon_type?: COUPON_TYPES;
  sub_total: number;
  tip: number;
  method_fee: number;
  payment_method_upcharge_fee: number;
  service_fee: number;
  total_discount: number;
  additional_tax: number;
  total: number;
  is_receipt_required: boolean;
  scheduled_for: null | string;
  paid_at: null | string;

  method: ORDER_METHODS;
  customer_phone_number: string;
  customer_email: string;
  customer_first_name: string;
  customer_last_name: string;
  order_instructions: null | string;
  room_service_details: null | string;

  fulfillment: null | OrderFulfillment;
  user?: UserInterface;
  coupon_details?: CouponDetailsInterface;
  line_items: OrderLineItem[];
  payment: Payment;
  delivery_quote?: DeliveryQuote;
  delivery?: Delivery;
  room_service_location?: RoomServiceLocation;
  rates: OrderRateInterface[];
  cashback_account_operations?: CashbackAccountOperation[];
  reward_account_operations?: RewardAccountOperation[];
};

export type Payment = {
  id: number;
  payment_method_id: PaymentMethod;
  payment_method_brand: null | PaymentMethodBrand;
  payment_method_details: null | string;
  amount: number;
  created_at: string;
};

export type Refund = {
  id: number;
  amount: number;
  created_at: string;
};

export type InvoiceData = {
  delivery_street_name: null | string;
  delivery_street_number: null | string;
  delivery_city: null | string;
  delivery_state: null | string;
  delivery_country: null | string;
  delivery_zip_code: null | string;
  delivery_dropoff_action: null | DropoffAction;
  delivery_doorbell_name: null | string;
  delivery_company_name: null | string;
  delivery_lat: null | number;
  delivery_lng: null | number;
  customer_note: null | string;

  invoice_company_name: null | string;
  invoice_first_name: null | string;
  invoice_last_name: null | string;
  invoice_address: null | string;
  invoice_zip_code: null | string;
  invoice_city: null | string;
  invoice_tax_id: null | string;
  invoice_email: null | string;
};

export type OrderData = Partial<Omit<Order, 'line_items'>> & {
  company_id?: number;
  payment_method_id?: PaymentMethod;
  payment_intent_id?: string;
  delivery_quote_id?: number;
  room_service_location_id?: number;

  room_service_details?: string;
  promo?: string | null;

  should_save_payment_info?: boolean;
  use_cashback?: boolean;
  marketing_consent?: boolean;

  line_items: BasketLineItem[];
  delivery_zone?: DeliveryZone;
  room_service_location?: RoomServiceLocation;
  utm?: UTMInterface | null;
};

export interface CashbackAccount {
  balance: number;
}

export interface RewardAccount {
  balance: number;
  rewards: RewardInterface[];
}

export interface CashbackAccountOperation {
  id: number;
  value: number;
  created_at: string;
}

export interface RewardAccountOperation {
  id: number;
  value: number;
  created_at: string;
}

export interface OrderRateInterface {
  id: number;
  user_id: number;
  user?: UserInterface;
  rate: number;
}

export enum ButtonTypes {
  primary = 'primary',
  secondary = 'secondary',
  third = 'third',
  forth = 'forth',
  fifth = 'fifth',
  sixth = 'sixth',
  disabled = 'disabled',
  danger = 'danger',
  warning = 'warning',
}

export enum ButtonSizes {
  normal = 'normal',
  small = 'small',
}

export interface LoginResponseInterface {
  auth_token: string;
  user: ProfileInterface;
}

export interface OneTimePasswordInterface {
  password?: string;
  expires_at: string;
  delivery_channel: OTP_DELIVERY_CHANNELS;
  delivery_address: string;
}

export interface ProfileInterface extends DeliveryAddressDataInterface {
  id?: number | null;
  phone_number?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  birthday?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  address?: string | null;
  zip_code?: string | null;
  lang?: string | null;
  timezone?: string | null;

  is_marketing_subscriber?: boolean;

  invoice_company_name?: string;
  invoice_first_name?: string;
  invoice_last_name?: string;
  invoice_address?: string;
  invoice_zip_code?: string;
  invoice_city?: string;
  invoice_tax_id?: string;
  invoice_email?: string;
}

export interface DeliveryAddressDataInterface {
  delivery_street_name?: string | null;
  delivery_street_number?: string | null;
  delivery_city?: string | null;
  delivery_state?: string | null;
  delivery_country?: string | null;
  delivery_zip_code?: string | null;
  delivery_dropoff_action?: DropoffAction | null;
  delivery_doorbell_name?: string | null;
  delivery_company_name?: string | null;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  customer_note?: string | null;
}

export interface CouponDetailsInterface {
  id?: number;
  code: string;
  referral_code_id?: number;
  discount_type: PROMO_CODE_DISCOUNT_TYPES;
  discount: number;
  type: PROMO_CODE_TYPES;
  expires_at: string;
  components: PromoComponentInterface[];
}

export interface PromoComponentInterface {
  id: number;
  discount: number;
  usage_threshold: number;
  is_used?: boolean;
}

export interface PromotionCodeResponseInterface {
  data: PromotionCodeDataInterface[];
  links: {
    first: string;
    last: string;
    prev: string;
    next: string;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: PaginatedLinkInterface[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

export interface PaginatedLinkInterface {
  url: string;
  label: string;
  active: boolean;
}

export interface PromotionCodeDataInterface {
  id: number;
  code: string;
  type: PROMO_CODE_TYPES;
  discount?: number;
  discount_type: PROMO_CODE_DISCOUNT_TYPES;
  expires_at: string;
  components: PromoComponentInterface[];
}

export interface PaymentIntent {
  id: string;
  client_secret: string;
}

export interface InitialDataResponseInterface {
  franchise: Franchise;
  company: CompanyDetailsInterface;
  profile: ProfileInterface;
  payment_options: PaymentOptionPropertiesInterface[];
}

export interface PusherConfig {
  key?: string;
  base_url?: string;
  cluster?: string;
}

export interface CompanyHoursHookInterface {
  open: boolean;
  openWholeDay: boolean;
  getCurrentWorkSchedule: () => OpeningHoursInterface | null;
  getNextWorkSchedule: () => OpeningHoursInterface | null;
  closedFrom: Moment;
  closedUntil: Moment;
  pickupClosed: boolean;
  deliveryClosed: boolean;
  orderTimeAvailable: boolean;
  opensToday: CompanyOpeningStatusInterface;
  opensStartingTomorrow: CompanyOpeningStatusInterface;
  ordersPausedUntil: string | null;
  orderArePausedTillEndOfDay: boolean;
  ordersPausedReason: ORDERS_PAUSED_REASON;
  ordersArePaused: boolean;
  ordersArePausedManually: boolean;
}

export interface CompanyOpeningStatusInterface {
  status: boolean;
  time: moment.Moment | null;
}

export interface FeedbackLabelInterface {
  message: string;
  type?: FeedbackLabelTypes;
  errors?: null | object;
  hideIcon?: boolean;
  className?: string;
  textClassname?: string;
}

export interface OrderMethodModalProps {
  openModal: boolean;
  orderMethod?: ORDER_METHODS;
  buttonTitle?: string;
  onComplete?: () => void;
}

export interface OtpVerificationModalProps {
  openModal: boolean;
  phoneNumber?: string;
  onSubmit?: (password: string) => Promise<boolean>;
}

export interface FreeProductsModalProps {
  openModal: boolean;
  onConfirm?: () => void;
}

export interface GlobalAppContextMenuInterface {
  screenPosition: number;
  activeCategoryId: number;
  menuLoaded: boolean;
}

export interface ReducerDispatchActionPayloadInterface<Type, Payload> {
  type: Type;
  payload?: Payload;
}

export interface AvailableLanguageInterface {
  language: string;
  languageName: string;
}

export interface RoutesInterface {
  path: string;
  element?: ReactElement;
  protectedType?: 'Auth';
  children?: RoutesInterface[];
  loader?: ReactElement;
}

export interface GoogleLocationInterface<Type> {
  success: boolean;
  location: Type | null;
}

export interface GoogleAddressParamsInterface {
  streetName: string;
  streetNumber: string;
  zip: string;
  city: string;
  state?: string;
  country: string;
}

export interface ScheduledOrderSlotInterface {
  start_time: string;
  end_time: string;
}

export interface ErrorHandlerInterface {
  [error_code: string | number]: () => void;
}

export interface CreateOrderRateInterface {
  rate: number;
}

export interface GoogleReviewStatusInterface {
  id: number;
  status: GOOGLE_REVIEW_STATUS_TYPES;
  name: string;
  screenshot_url: string;
  created_at: string;
}

export interface ActionPayloadInterface<Type> {
  payload: Type;
  type: string;
}

export interface MenuItemModalProps {
  openModal: boolean;
  isFreeItem?: boolean;
  product: Partial<ProductInterface> | null;
  lineItem?: BasketLineItem | null;
  screenPosition?: number;
  onCloseModal?: () => void;
}

export interface LoginModalProps {
  openModal: boolean;
  isStatic?: boolean;
  title?: string | ReactElement;
  initialPhoneNumber?: string;
  onLoginSuccess?: () => void;
  onCloseRedirect?: (isLoggedIn: boolean) => void;
}

export interface SearchDeliveryZoneParams {
  company_id: number;
  address?: null | string;
  zip?: null | string;
  lat?: null | number;
  lng?: null | number;
  throw?: boolean;
}

export interface CreateDeliveryQuotesParams {
  dropoff_time: null | string | Moment;
  street_name: string;
  street_number: string;
  city: string;
  state: null | string;
  country: string;
  zip_code: string;
  lat: null | number;
  lng: null | number;
  instructions?: null | string;
}

export interface GeolocationInterface {
  lat: number;
  lng: number;
}

export interface FormatCurrencyOptions {
  currency?: string;
  locale?: string;
  trimInteger?: boolean;
}

export interface UTMInterface {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_tracking_link_id: string | null;
  gclid: string | null;
  fbclid: string | null;
}
